export default function fundingOptionsBlock() {
	const blocks = document.querySelectorAll(".funding-options-block");
	if (!blocks.length) return;

	blocks.forEach((block) => {
		const graphItems = Array.from(block.querySelectorAll(".items-graph .item"));
		const contentItems = Array.from(block.querySelectorAll(".items-content .item"));
		const prevBtn = block.querySelector(".nav-prev");
		const nextBtn = block.querySelector(".nav-next");
		const track = block.querySelector(".funding-spectrum .spectrum-track");
		const hit = block.querySelector(".funding-spectrum .spectrum-hit");
		if (!graphItems.length || !contentItems.length || !track || !hit) return;

		const indices = graphItems
			.map((el) => Number(el.dataset.index))
			.filter((n) => !Number.isNaN(n))
			.sort((a, b) => a - b);

		const minIndex = indices[0] ?? 0;
		const maxIndex = indices[indices.length - 1] ?? graphItems.length - 1;
		const steps = Math.max(0, maxIndex - minIndex);

		const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

		const indexToProgress = (index) => {
			if (steps <= 0) return 0;
			return ((index - minIndex) / steps) * 100;
		};

		const progressToIndex = (progressPct) => {
			if (steps <= 0) return minIndex;
			const raw = (progressPct / 100) * steps + minIndex;
			return clamp(Math.round(raw), minIndex, maxIndex);
		};

		const getActiveIndex = () => {
			const stored = Number(block.dataset.activeIndex);
			if (!Number.isNaN(stored)) return stored;
			const current = graphItems.find((el) => el.classList.contains("is-active"));
			const idx = current ? Number(current.dataset.index) : minIndex;
			return Number.isNaN(idx) ? minIndex : idx;
		};

		const setProgressCSS = (progressPct) => {
			block.style.setProperty("--spectrum-progress", `${clamp(progressPct, 0, 100)}%`);
		};

		const getProgressCSS = () => {
			const v = parseFloat(getComputedStyle(block).getPropertyValue("--spectrum-progress"));
			return Number.isFinite(v) ? v : indexToProgress(getActiveIndex());
		};

		const setAria = (index) => {
			track.setAttribute("aria-valuemin", String(minIndex + 1));
			track.setAttribute("aria-valuemax", String(maxIndex + 1));
			track.setAttribute("aria-valuenow", String(index + 1));
		};

		const applyActiveClasses = (index) => {
			const i = clamp(index, minIndex, maxIndex);

			graphItems.forEach((el) => {
				const isActive = Number(el.dataset.index) === i;
				el.classList.toggle("is-active", isActive);
				el.classList.toggle("is-inactive", !isActive);
			});

			contentItems.forEach((el) => {
				const isActive = Number(el.dataset.index) === i;
				el.classList.toggle("is-active", isActive);
			});

			prevBtn?.classList.toggle("is-disabled", i === minIndex);
			nextBtn?.classList.toggle("is-disabled", i === maxIndex);

			setAria(i);
			block.dataset.activeIndex = String(i);
		};

		let animId = 0;

		const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

		const animateProgress = (toPct, duration = 220) => {
			if (animId) cancelAnimationFrame(animId);
			const fromPct = getProgressCSS();
			const start = performance.now();

			const tick = (now) => {
				const t = clamp((now - start) / duration, 0, 1);
				const pct = fromPct + (toPct - fromPct) * easeOutCubic(t);
				setProgressCSS(pct);
				if (t < 1) animId = requestAnimationFrame(tick);
				else animId = 0;
			};

			animId = requestAnimationFrame(tick);
		};

		const setActive = (index, { animateBar = false } = {}) => {
			const i = clamp(index, minIndex, maxIndex);
			applyActiveClasses(i);
			const targetPct = indexToProgress(i);
			if (animateBar) animateProgress(targetPct);
			else setProgressCSS(targetPct);
		};

		const getProgressFromClientX = (clientX) => {
			const rect = track.getBoundingClientRect();
			const x = clamp(clientX - rect.left, 0, rect.width);
			return rect.width ? (x / rect.width) * 100 : 0;
		};

		graphItems.forEach((el) => {
			el.addEventListener("click", () => {
				const idx = Number(el.dataset.index);
				if (Number.isNaN(idx)) return;
				setActive(idx, { animateBar: true });
			});
		});

		prevBtn?.addEventListener("click", () => setActive(getActiveIndex() - 1, { animateBar: true }));
		nextBtn?.addEventListener("click", () => setActive(getActiveIndex() + 1, { animateBar: true }));

		const clickSet = (clientX) => {
			const pct = getProgressFromClientX(clientX);
			const snappedIndex = progressToIndex(pct);
			setActive(snappedIndex, { animateBar: true });
		};

		track.addEventListener("click", (e) => clickSet(e.clientX));

		let dragging = false;
		let rafId = 0;
		let latestPct = null;
		let lastSnapped = null;

		const paint = () => {
			rafId = 0;
			if (latestPct == null) return;
			setProgressCSS(latestPct);
			const snapped = progressToIndex(latestPct);
			if (snapped !== lastSnapped) {
				lastSnapped = snapped;
				applyActiveClasses(snapped);
			}
		};

		const onMove = (clientX) => {
			if (animId) cancelAnimationFrame(animId);
			animId = 0;
			latestPct = getProgressFromClientX(clientX);
			if (!rafId) rafId = requestAnimationFrame(paint);
		};

		const endDrag = () => {
			if (!dragging) return;
			dragging = false;
			block.classList.remove("is-dragging");

			const pct = latestPct == null ? getProgressCSS() : latestPct;
			const snappedIndex = progressToIndex(pct);
			const snappedPct = indexToProgress(snappedIndex);

			applyActiveClasses(snappedIndex);
			animateProgress(snappedPct, 180);

			latestPct = null;
			lastSnapped = null;
			if (rafId) cancelAnimationFrame(rafId);
			rafId = 0;
		};

		const startDrag = (clientX) => {
			dragging = true;
			block.classList.add("is-dragging");
			onMove(clientX);
		};

		if ("PointerEvent" in window) {
			hit.addEventListener("pointerdown", (e) => {
				track.setPointerCapture?.(e.pointerId);
				startDrag(e.clientX);

				const move = (ev) => {
					if (!dragging) return;
					ev.preventDefault();
					onMove(ev.clientX);
				};

				const up = () => {
					document.removeEventListener("pointermove", move);
					document.removeEventListener("pointerup", up);
					endDrag();
				};

				document.addEventListener("pointermove", move, { passive: false });
				document.addEventListener("pointerup", up);
			});

			track.addEventListener("pointerdown", (e) => {
				startDrag(e.clientX);

				const move = (ev) => {
					if (!dragging) return;
					ev.preventDefault();
					onMove(ev.clientX);
				};

				const up = () => {
					document.removeEventListener("pointermove", move);
					document.removeEventListener("pointerup", up);
					endDrag();
				};

				document.addEventListener("pointermove", move, { passive: false });
				document.addEventListener("pointerup", up);
			});
		} else {
			hit.addEventListener("mousedown", (e) => {
				startDrag(e.clientX);

				const move = (ev) => {
					if (!dragging) return;
					ev.preventDefault();
					onMove(ev.clientX);
				};

				const up = () => {
					document.removeEventListener("mousemove", move);
					document.removeEventListener("mouseup", up);
					endDrag();
				};

				document.addEventListener("mousemove", move);
				document.addEventListener("mouseup", up);
			});

			hit.addEventListener("touchstart", (e) => {
				const t = e.touches[0];
				if (!t) return;
				startDrag(t.clientX);
			}, { passive: true });

			hit.addEventListener("touchmove", (e) => {
				if (!dragging) return;
				const t = e.touches[0];
				if (!t) return;
				onMove(t.clientX);
			}, { passive: true });

			hit.addEventListener("touchend", () => endDrag(), { passive: true });

			track.addEventListener("mousedown", (e) => startDrag(e.clientX));
		}

		track.addEventListener("keydown", (e) => {
			const current = getActiveIndex();
			if (e.key === "ArrowLeft") {
				e.preventDefault();
				setActive(current - 1, { animateBar: true });
			}
			if (e.key === "ArrowRight") {
				e.preventDefault();
				setActive(current + 1, { animateBar: true });
			}
			if (e.key === "Home") {
				e.preventDefault();
				setActive(minIndex, { animateBar: true });
			}
			if (e.key === "End") {
				e.preventDefault();
				setActive(maxIndex, { animateBar: true });
			}
		});

		setActive(minIndex);
	});
}